import { computed, ref } from "vue"
import { defineStore } from "pinia"
import { api, ApiError, http } from "@/lib/api"
import type { AccountRole, LoginResult, UserView, WebsiteSessionView } from "@/types/api"

const ROLE_LEVEL: Record<AccountRole, number> = { CUSTOMER: 0, SUPPORT: 1, ADMIN: 2, SUPER_ADMIN: 3 }

export const useAuthStore = defineStore("auth", () => {
  const user = ref<UserView | null>(null)
  const session = ref<WebsiteSessionView | null>(null)
  const loaded = ref(false)
  const loading = ref(false)
  const enrollmentRequired = ref(false)

  const authenticated = computed(() => user.value !== null && session.value !== null)
  const mfaComplete = computed(() => session.value?.mfaVerified === true || user.value?.role === "CUSTOMER")
  const isOperator = computed(() => !!user.value && ROLE_LEVEL[user.value.role] >= ROLE_LEVEL.SUPPORT)
  const canAdmin = computed(() => !!user.value && ROLE_LEVEL[user.value.role] >= ROLE_LEVEL.ADMIN && mfaComplete.value)
  const isSuperAdmin = computed(() => user.value?.role === "SUPER_ADMIN" && mfaComplete.value)

  function hasRole(minimum: AccountRole): boolean {
    return !!user.value && ROLE_LEVEL[user.value.role] >= ROLE_LEVEL[minimum]
  }

  function accept(result: LoginResult): void {
    if (result.state === "MFA_REQUIRED") return
    user.value = result.user
    session.value = result.session
    enrollmentRequired.value = result.state === "MFA_ENROLLMENT_REQUIRED"
    loaded.value = true
    http.clearSessionSecurityState()
  }

  function clear(): void {
    user.value = null
    session.value = null
    enrollmentRequired.value = false
    loaded.value = true
    http.clearSessionSecurityState()
  }

  async function loadSession(force = false): Promise<void> {
    if ((loaded.value && !force) || loading.value) return
    loading.value = true
    try {
      accept(await api.auth.session())
    } catch (error) {
      if (error instanceof ApiError && error.problem.status === 401) clear()
      else throw error
    } finally {
      loaded.value = true
      loading.value = false
    }
  }

  async function logout(): Promise<void> {
    try {
      await api.auth.logout()
    } finally {
      clear()
    }
  }

  return {
    user,
    session,
    loaded,
    loading,
    enrollmentRequired,
    authenticated,
    mfaComplete,
    isOperator,
    canAdmin,
    isSuperAdmin,
    hasRole,
    accept,
    clear,
    loadSession,
    logout,
  }
})
